// src/features/battle/BattlePanel.tsx
import { useState, useEffect } from 'react';
import { CATEGORY_LABEL, CATEGORY_COLOR } from '../../core/constants';
import { useTeamStore } from '../../store/useTeamStore';
import { useScenarioStore } from '../../store/useScenarioStore';
import { SKILL_MAP } from '../../data/mock-skills';
import { SPIRIT_MAP } from '../../data/mock-spirits';
import { calculateDamage } from '../../core/damage';
import { calculateStats } from '../../core/stats';
import { NATURE_MAP } from '../../data/mock-natures';
import type { Skill, DamageResult, Spirit } from '../../core/types';
import { StatBlock } from '../../components/StatBlock';

type SkillResultItem = {
  skillId: string;
  skillName: string;
  result: DamageResult;
};

export function BattlePanel() {
  const team = useTeamStore((s) => s.team);
  const attackerIndex = useScenarioStore((s) => s.attackerIndex);
  const defenderIndex = useScenarioStore((s) => s.defenderIndex);
  const setSkill = useScenarioStore((s) => s.setSkill);

  const attackerSlot = team.slots[attackerIndex];
  const defenderSlot = team.slots[defenderIndex];

  const attackerSpirit: Spirit | undefined = attackerSlot.spiritId ? SPIRIT_MAP[attackerSlot.spiritId] : undefined;
  const defenderSpirit: Spirit | undefined = defenderSlot.spiritId ? SPIRIT_MAP[defenderSlot.spiritId] : undefined;

  // 自定义技能状态
  const [customPower, setCustomPower] = useState<number>(100);
  const [customCategory, setCustomCategory] = useState<'physical' | 'special'>('physical');
  const [customType, setCustomType] = useState<string>('普通'); // 新增：技能系别
  // 多个技能结果列表
  const [skillResults, setSkillResults] = useState<SkillResultItem[]>([]);

  // 切换攻守/精灵时清空全部计算结果
  useEffect(() => {
    setSkillResults([]);
  }, [attackerIndex, defenderIndex]);

  if (!attackerSpirit || !defenderSpirit) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16 text-slate-500">
        请先在「配置」页选择攻击方与防守方
      </div>
    );
  }

  const attackerStats = calculateStats(
    attackerSpirit.baseStats,
    attackerSlot.ivs,
    attackerSlot.evs,
    NATURE_MAP[attackerSlot.nature]
  );
  const defenderStats = calculateStats(
    defenderSpirit.baseStats,
    defenderSlot.ivs,
    defenderSlot.evs,
    NATURE_MAP[defenderSlot.nature]
  );

  /** 计算自定义技能，追加/更新到结果列表 */
  function runCalcCustomSkill() {
    const tempSkill: Skill = {
      id: 'custom-skill',
      name: `自定义${customCategory === 'physical' ? '物理' : '魔法'}(威力${customPower})`,
    type: customType, // ✅ 使用选中的系别
      power: customPower,
      category: customCategory,
      accuracy: 100,
    };

    const damageInput = {
      attacker: {
        spirit: attackerSpirit!,
        stats: attackerStats,
        boosts: attackerSlot.boosts,
        skill: tempSkill,
      },
      defender: {
        spirit: defenderSpirit!,
        stats: defenderStats,
        boosts: defenderSlot.boosts,
      },
      field: {crit: false,weather: 'none',},
    };
    let res = calculateDamage(damageInput);

  
    // ========= 修复点：先定义局部变量，不要直接返回裸对象字面量 =========
  const newCustomItem: SkillResultItem = {
    skillId: 'custom-skill',
    skillName: `自定义${customType} ${customCategory === 'physical' ? '物理' : '魔法'}(威力${customPower})`,
    result: res
  };

  setSkillResults((prev) => {
    const rest = prev.filter(item => item.skillId !== 'custom-skill');
    return [...rest, newCustomItem]; // 返回已经有类型的变量
  });
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* 对战信息 */}
      <div className="p-4 rounded-xl border border-slate-700 bg-slate-800 flex items-center justify-between gap-4 text-sm">
        <div className="text-red-400 font-semibold truncate">
          {attackerSpirit.name}
        </div>
        <div className="text-slate-500">vs</div>
        <div className="text-blue-400 font-semibold truncate">
          {defenderSpirit.name}
        </div>
      </div>

      {/* 选择精灵自带技能 */}
      <div className="p-4 rounded-xl border border-slate-700 bg-slate-800">
        <h3 className="text-sm font-semibold mb-3 text-slate-300">
          选择技能
        </h3>
        {attackerSlot.skills.length ? (
          <div className="flex flex-wrap gap-2">
            {attackerSlot.skills.map((sid) => {
              const skill = SKILL_MAP[sid];
              return (
                <button
                  key={sid}
                  onClick={() => {
                    setSkill(sid);
                    if (skill) {
                      const damageInput = {
                        attacker: {
                          spirit: attackerSpirit!,
                          stats: attackerStats,
                          boosts: attackerSlot.boosts,
                          skill: skill,
                        },
                        defender: {
                          spirit: defenderSpirit!,
                          stats: defenderStats,
                          boosts: defenderSlot.boosts,
                        },
                        field: { crit: false, weather: 'none' },
                      };
                      const dmgRes = calculateDamage(damageInput);
                      const newItem: SkillResultItem = {
                        skillId: sid,
                        skillName: skill.name,
                        result: dmgRes
                      };
                      setSkillResults(prev => {
                        const rest = prev.filter(i => i.skillId !== sid);
                        return [...rest, newItem];
                      });
                    }
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm transition ${
                    skill?.id === sid
                      ? 'bg-primary-600 text-white'
                      : 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                  }`}
                >
                  <span>{skill?.name ?? sid}</span>
                  <span
                    className={`px-1.5 py-0.5 text-[9px] rounded-full border ${CATEGORY_COLOR[skill?.category ?? 'status']}`}
                  >
                    {CATEGORY_LABEL[skill?.category ?? 'status']}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-slate-500 text-sm">
            请先在「配置」页给攻击方配置技能
          </p>
        )}
      </div>

      {/* 自定义技能区域 */}
      <div className="p-4 rounded-xl border border-slate-700 bg-slate-800">
        <h3 className="text-sm font-semibold mb-3 text-slate-300">
          自定义技能
        </h3>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-slate-300 text-sm">威力:</span>
            <input
              type="number"
              min={0}
              value={customPower}
              onChange={(e) => setCustomPower(Number(e.target.value || 0))}
              className="w-20 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-300 text-sm">伤害类型:</span>
            <select
              value={customCategory}
              onChange={(e) => setCustomCategory(e.target.value as 'physical' | 'special')}
              className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white"
            >
              <option value="physical">物理</option>
              <option value="special">魔法</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-300 text-sm">系别:</span>
            <select
              value={customType}
              onChange={(e) => setCustomType(e.target.value)}
              className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white"
            >
              <option value="普通">普通</option>
              <option value="火">火</option>
              <option value="水">水</option>
              <option value="草">草</option>
              <option value="电">电</option>
              <option value="冰">冰</option>
              <option value="武">武</option>
              <option value="毒">毒</option>
              <option value="土">土</option>
              <option value="翼">翼</option>
              <option value="萌">萌</option>
              <option value="虫">虫</option>
              <option value="石">石</option>
              <option value="幽">幽</option>
              <option value="龙">龙</option>
              <option value="恶">恶</option>
              <option value="光">光</option>
            </select>
          </div>
          <button
            onClick={runCalcCustomSkill}
            className="bg-blue-600 hover:bg-blue-500 px-3 py-1.5 rounded text-white text-sm"
          >
            计算自定义技能
          </button>
        </div>
      </div>

      {/* 多技能结果对比面板 */}
      <div className="p-4 rounded-xl border border-slate-700 bg-slate-800">
        <h3 className="text-sm font-semibold mb-3 text-slate-300">
          计算结果
        </h3>
        {skillResults.length > 0 ? (
          <div className="space-y-4">
            {skillResults.map((item) => (
              <div key={item.skillId} className="border border-slate-700 rounded-lg p-3 bg-slate-900/40">
                <div className="text-sm text-cyan-300 mb-2">{item.skillName}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <StatBlock label="最小伤害" value={item.result.min} />
                  <StatBlock label="最大伤害" value={item.result.max} />
                  <StatBlock label="平均伤害" value={item.result.avg} />
                  <StatBlock
                    label="伤害占比"
                    value={`${item.result.percentMin.toFixed(1)}% ~ ${item.result.percentMax.toFixed(1)}%`}
                  />
                  <StatBlock
                    label="击杀概率"
                    value={`${(item.result.koChance * 100).toFixed(1)}%`}
                    highlight={item.result.koChance >= 1}
                  />
                  <StatBlock label="剩余 HP" value={`${item.result.remainingHp}`} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-sm">点击技能或者计算自定义技能查看伤害</p>
        )}
      </div>
    </div>
  );
}


